<!-- ビデオプレイヤー -->
<script setup lang="ts">
  import _ from "lodash";
  import { storeToRefs } from "pinia";
  import { VideoPlayer } from "@videojs-player/vue";
  import "video.js/dist/video-js.css";
  import { useVideoStateStore } from "~/stores/VideoStateStore";
  import { useLoginStore } from "~/stores/LoginStore";
  import { useContentsStore } from "~/stores/ContentsStore";

  // ビデオ周りは複雑なレイアウトなのでステート管理にpiniaを使用する
  const videoStateStore = useVideoStateStore();

  interface Props {
    contentsId?: string;
    contentsUrl?: string;
    contentsSubUrl?: string;
    thumbnailImage?: string;
    autoplay?: boolean;
    embed?: boolean; // embedをtrue指定すると再生ボタンが表示される
    muted?: boolean;
    duration?: number;
    isComplete?: boolean;
  }
  const props = defineProps<Props>();

  interface Emits {
    (event: "update:hasError", hasError: boolean): void;
  }
  const emit = defineEmits<Emits>();

  const loginStore = useLoginStore();
  const contentsStore = useContentsStore();
  const { userContentsInfo } = storeToRefs(contentsStore);

  // ログイン中は再生時間を読み書き
  const syncCurrentTime = props.contentsId && loginStore.isLogin();
  if (syncCurrentTime) {
    contentsStore.fetchUserContentsInfo(loginStore.loginInfo!.userId, props.contentsId);
  }

  let putLastPlayedTimeSecValue: number = 0;

  // 完了フラグをポスト済みかどうか
  const postedComplete = ref(false);
  // 完了フラグ
  const isComplete = computed<boolean>(() => {
    // コンテンツの完了フラグがtrueの場合、またはこの画面内で完了フラグをポスト済みの場合はtrue
    return props.isComplete || postedComplete.value;
  });

  // 重複リクエストを防ぐために debounce を使用
  const putCurrentTime = _.debounce(
    async (
      userId: string,
      contentsId: string,
      currentTime: number,
      duration: number | undefined,
      isComplete: boolean,
    ) => {
      await contentsStore.putCurrentTime(userId, contentsId, currentTime, duration, isComplete);
      putLastPlayedTimeSecValue = currentTime;
      if (duration && currentTime >= duration) {
        // コンテンツのデータを再読み込みするのは負荷がかかる&制御が複雑なため、このコンポーネント内で完了フラグを管理する
        postedComplete.value = true;
      }
    },
    500,
  );

  const lastPlayedTimeSec = computed<number>({
    get: () => (userContentsInfo.value ? userContentsInfo.value.currentTime : 0),
    set: value => {
      if (syncCurrentTime) {
        // 書き込み周期は 10秒
        // https://arkad-co.slack.com/archives/C06P8P9L95G/p1711687471448399?thread_ts=1711443769.860049&cid=C06P8P9L95G
        // 前回の put から 10 秒以上差がある or duration以上であれば 書き込む
        const duration: number | undefined = videoPlayer.value?.duration();
        if (Math.abs(value - putLastPlayedTimeSecValue) > 10 || (!isComplete.value && duration && value >= duration)) {
          putCurrentTime(loginStore.loginInfo!.userId, props.contentsId, value, duration, isComplete.value);
        }
      }
    },
  });

  // 埋め込み動画用の再生ボタン表示フラグ
  const showEmbedPlayButton = ref<boolean>(props.embed);

  // エラーダイアログ表示フラグ
  const showErrorDialog = ref<boolean>(false);

  // 音声ボリューム
  const muted = ref<boolean>(props.muted);

  const videoPlayer = ref<any | undefined>(undefined);
  const videoCurrentTime = ref(0);

  // 実際に再生するURL。contentsUrlが取得できない場合はcontentsSubUrlを使用する
  const displayedContentsUrl = ref<string | undefined>(undefined);
  // S3から取得した動画のMIMEタイプ
  const contentType = ref<string | undefined>(undefined);
  const setVideo = ref(false);
  const playbackRate = ref(1);
  const volume = ref(100);

  const onVideoMounted = (props: any) => {
    videoPlayer.value = props.player;
  };

  // 映像右下に表示する再生時間の位置調整
  const embedDuration = ref<HTMLElement | null>(null);
  const embedDurationRight = ref<string | undefined>();
  const embedResizeObserver = ref<ResizeObserver | null>(null);
  const loadedMetadata = ref(false);
  const onLoadedMetadata = () => {
    // video の width, height がこのタイミングで決まるので、それをもとに再生時間の位置を調整する
    // windowのリサイズにも対応するため、ResizeObserverを使用する
    // props.durationが空の場合はembedDurationは非表示となる
    if (props.duration) {
      embedResizeObserver.value = new ResizeObserver(entries => {
        const aspectRatio = videoPlayer.value.videoWidth() / videoPlayer.value.videoHeight();
        const parentRect = entries[0].contentRect;
        embedDurationRight.value = `${(parentRect.width - parentRect.height * aspectRatio) / 2}px`;
      });
      embedResizeObserver.value.observe(embedDuration.value!.parentElement as HTMLElement);
    }
    // 途中から再生する場合 (iOS 以外)
    if (!isIOS() && lastPlayedTimeSec.value !== 0) {
      videoPlayer.value.currentTime(lastPlayedTimeSec.value);
    }
    loadedMetadata.value = true;
  };
  const onLoadedData = () => {
    // 途中から再生する場合
    if (isIOS() && lastPlayedTimeSec.value !== 0) {
      videoPlayer.value.currentTime(lastPlayedTimeSec.value);
      onTimeUpdate(); // ios ではこれを明示的に呼ばないと lastPlayedTimeSec が反映されない
    }
  };
  onUnmounted(async () => {
    embedResizeObserver.value?.disconnect();
    const isPictureInPicture = document.pictureInPictureElement !== null;
    if (videoPlayer.value && isPictureInPicture) {
      await videoPlayer.value.exitPictureInPicture();
    }
  });

  const onTimeUpdate = () => {
    if (videoPlayer.value) {
      videoCurrentTime.value = videoPlayer.value.currentTime();
      videoStateStore.setCurrentTime(videoPlayer.value.currentTime());
      lastPlayedTimeSec.value = videoPlayer.value.currentTime();
    }
  };

  const onError = () => {
    emit("update:hasError", true);
  };

  const currentTime = computed<number>({
    get: () => {
      if (videoPlayer.value) {
        return videoCurrentTime.value;
      }
      return 0;
    },
    set: value => {
      if (videoPlayer.value && !isNaN(value)) {
        videoPlayer.value.currentTime(value);
        videoCurrentTime.value = value;
      }
    },
  });

  const clickEmbedPanel = () => {
    // 埋め込み用再生ボタンを非表示にしてから再生する
    showEmbedPlayButton.value = false;
    clickPlay();
  };

  const clickPlay = () => {
    if (videoPlayer.value) {
      if (videoPlayer.value.paused()) {
        videoPlayer.value.play();
      } else {
        videoPlayer.value.pause();
      }
    }
  };
  const clickFullscreen = () => {
    if (videoPlayer.value) {
      if (videoPlayer.value.isFullscreen()) {
        videoPlayer.value.exitFullscreen();
      } else {
        videoPlayer.value.requestFullscreen();
      }
    }
  };
  const clickMuteAudio = () => {
    muted.value = !muted.value;
  };

  const clickPip = () => {
    if (videoPlayer.value) {
      videoPlayer.value.requestPictureInPicture();
    }
  };

  const clickMove = (value: number) => {
    videoPlayer.value.currentTime(videoPlayer.value.currentTime() + value);
  };

  // videojsプレイヤーではS3署名付きURLを再生する場合、contentTypeを指定しなければならない
  // そのためMimeTypeを取得する処理を追加している
  const getContentType = async () => {
    if (props.contentsUrl?.length) {
      const res = await GetContentType(props.contentsUrl);
      if (res.statusCode === 200) {
        contentType.value = res.contentType;
        displayedContentsUrl.value = props.contentsUrl;
      } else if (props.contentsSubUrl?.length) {
        // contentsUrlが取得できない場合はcontentsSubUrlを使用する
        const res2 = await GetContentType(props.contentsSubUrl);
        if (res2.statusCode === 200) {
          contentType.value = res2.contentType;
          displayedContentsUrl.value = props.contentsSubUrl;
        }
      }
      if (!contentType.value) {
        sentryErrorLog(`動画視聴の接続失敗 :  ${JSON.stringify(res.error)}`);
        showErrorDialog.value = true;
      }
    }
  };

  getContentType();

  watch(
    () => {
      return props.contentsUrl;
    },
    async () => {
      await getContentType();
    },
  );

  watchEffect(() => {
    if (setVideo.value) {
      return;
    }
    if (displayedContentsUrl.value && contentType.value && videoPlayer.value) {
      // videojsプレイヤーには色々制限があるため、わざわざsrcメソッドを読んでセットしている
      // videojsプレイヤーではS3署名付きURLを再生する場合、contentTypeを指定しなければ再生できない
      // <VideoPlayer>タグではsrcとtypeを両方セットすることができない
      // sources: [{ src: props.contentsUrl, type: contentType.value }]と指定することができるが、この指定ではフルスクリーン時になぜか再生がリセットされてしまう。
      // videojsプレイヤーは以下のmimeTypeしかサポートしていないため、video/quicktimeなどは'video/mp4'として再生する
      let mimeType: string | boolean = contentType.value;
      switch (mimeType) {
        case "video/ogg":
        case "video/mp4":
        case "video/webm":
          break;
        default:
          mimeType = "video/mp4";
      }
      videoPlayer.value.src({ src: displayedContentsUrl.value, type: mimeType });
      setVideo.value = true;
    }
  });

  // memo: iosでは playsinlineでautoplayをしたい場合にはmutedを指定しなければいけない
</script>

<template>
  <VideoPlayer
    v-if="displayedContentsUrl && contentType"
    fill
    :volume="volume / 100"
    :autoplay="autoplay"
    :playsinline="true"
    :muted="muted"
    :poster="thumbnailImage"
    :playback-rate="playbackRate"
    @mounted="onVideoMounted"
    @loadedmetadata="onLoadedMetadata"
    @loadeddata="onLoadedData"
    @timeupdate="onTimeUpdate"
    @error="onError"
  >
    <template #default>
      <template v-if="showEmbedPlayButton">
        <v-sheet
          v-show="loadedMetadata"
          class="embed-controls w-100 h-100"
          color="transparent"
          @click="clickEmbedPanel"
        >
          <AtomsSvgIcon
            class="embed-play-button"
            data-testid="embed-play-button"
            :width="70"
            :height="70"
            img-path="assets/images/icon/embed-play.svg"
          />
          <div v-if="props.duration" v-show="embedDurationRight" ref="embedDuration" class="embed-duration">
            {{ formatMoviePlayTime(props.duration!) }}
          </div>
        </v-sheet>
      </template>
      <template v-else>
        <MoleculesVideoControls
          v-model:current-time="currentTime"
          v-model:playback-rate="playbackRate"
          v-model:volume="volume"
          :max-time="videoPlayer?.duration()"
          :fullscreen="videoPlayer?.isFullscreen()"
          :paused="videoPlayer?.paused()"
          :is-audio-muted="muted"
          :show-play-button="true"
          :show-slider="true"
          :show-fullscreen-button="true"
          mute-button-type="audio"
          @click-play="clickPlay"
          @click-fullscreen="clickFullscreen"
          @click-mute-audio="clickMuteAudio"
          @click-pip="clickPip"
          @click-move="clickMove"
        />
        <slot></slot>
      </template>
    </template>
  </VideoPlayer>
  <div v-else-if="showErrorDialog">
    <MoleculesContentsDialog message="動画の取得に失敗しました。時間をおいて再度お試しください。" button-title="" />
  </div>
</template>

<style lang="scss" scoped>
  :deep(.vjs-error-display) {
    display: none;
  }

  .embed-controls {
    z-index: 2;
    position: absolute;
    top: 0;
    cursor: pointer;
    .embed-play-button {
      height: 70px;
      width: 70px;
      display: block;
      position: absolute;
      top: 50%;
      left: 50%;
      padding: 0;
      background-color: rgba(0, 0, 0, 0);
      transform: translate(-50%, -50%);
      -webkit-transform: translate(-50%, -50%);
      -ms-transform: translate(-50%, -50%);
    }
  }

  .embed-duration {
    position: absolute;
    bottom: 0;
    right: 0;
    margin: 16px;
    text-align: center;
    padding-top: 4px;
    background-color: rgb(var(--v-theme-monochrome-04));
    font-size: 12px;
    font-weight: 700;
    opacity: 90%;
    border-radius: 100px;
    width: 64px;
    height: 20px;
  }
</style>
